import { useCallback, useEffect, useState } from 'react';

const STORAGE_PREFIX = 'zeettu.searchFields.';

function readStored(screenId: string, defaults: readonly string[]): string[] {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${screenId}`);
    if (!raw) return [...defaults];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [...defaults];
    const allowed = new Set(defaults);
    const filtered = parsed
      .map((v) => String(v ?? '').trim())
      .filter((v) => v && allowed.has(v));
    // Empty saved set → treat as all (never search zero fields).
    return filtered.length > 0 ? filtered : [...defaults];
  } catch {
    return [...defaults];
  }
}

/**
 * Persist which document fields participate in text search for a screen.
 * Defaults to all `defaultFields`. Empty selection falls back to all.
 */
export function usePersistedSearchFields(
  screenId: string,
  defaultFields: readonly string[],
): {
  searchFields: string[];
  setSearchFields: (next: string[]) => void;
  toggleField: (field: string) => void;
  allFields: readonly string[];
} {
  const [searchFields, setSearchFieldsState] = useState<string[]>(() =>
    readStored(screenId, defaultFields),
  );

  useEffect(() => {
    setSearchFieldsState(readStored(screenId, defaultFields));
  }, [screenId, defaultFields]);

  const setSearchFields = useCallback(
    (next: string[]) => {
      const allowed = new Set(defaultFields);
      const filtered = next.map((v) => String(v ?? '').trim()).filter((v) => allowed.has(v));
      const value = filtered.length > 0 ? filtered : [...defaultFields];
      setSearchFieldsState(value);
      try {
        localStorage.setItem(`${STORAGE_PREFIX}${screenId}`, JSON.stringify(value));
      } catch {
        /* ignore quota / private mode */
      }
    },
    [screenId, defaultFields],
  );

  const toggleField = useCallback(
    (field: string) => {
      if (!defaultFields.includes(field)) return;
      setSearchFieldsState((prev) => {
        const has = prev.includes(field);
        let next = has ? prev.filter((f) => f !== field) : [...prev, field];
        if (next.length === 0) next = [...defaultFields];
        try {
          localStorage.setItem(`${STORAGE_PREFIX}${screenId}`, JSON.stringify(next));
        } catch {
          /* ignore */
        }
        return next;
      });
    },
    [screenId, defaultFields],
  );

  return {
    searchFields,
    setSearchFields,
    toggleField,
    allFields: defaultFields,
  };
}

/** Serialize selected fields for API query params. */
export function searchFieldsQueryValue(fields: string[], allFields: readonly string[]): string | undefined {
  if (!fields.length || fields.length >= allFields.length) {
    const allSelected = allFields.every((f) => fields.includes(f));
    if (allSelected || !fields.length) return undefined;
  }
  return fields.join(',');
}
